class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      // Sidebar filters selectors
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper",
    };
    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content",
    };
    this.sectionId = "";
    this.isDesignMode = MinimogSettings.design_mode;

    this.currentPage = 1;
    this.paginationType; // "paginate" | "loadmore" | "infinite"
    this.totalPages;
    this.totalProducts;
    this.infiniteLoadingObserver;

    this.showColSwitchers;
    this.cachedCol;
    this.initialCol;
    this.activeCol;
    this.STORAGE_KEY = "gridColumnViews";
    this.STORAGE_KEY_SCROLL = "collectionScrollState"; // NUEVO
    this.MAX_COL_BY_SCREEN_SIZE = {
      mobile: 2,
      tablet: 4,
      desktop: 5,
    };

    this.enableFilters;
    this.enableSorting;
    this.filtersType; // "storefront_filters" | "tags_filter"
    this.filtersPosition; // "leftColumn" | "rightColumn" | "fixed"
    this.accordions;
    this.sideEffectEventsAdded = false;
  }

  init = () => {
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);
    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();
    this.initScrollRestoration(); // NUEVO
    this.sideEffectEventsAdded = true;
    MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => refreshProductReview());

    // Handle sticky toolbar on mobile
    this.handleStickyToolbar();
    document.addEventListener("matchMobile", () => this.handleStickyToolbar());
    document.addEventListener("unmatchMobile", () => this.handleStickyToolbar());
  };

  setData = () => {
    const { section, productContainer } = this.domNodes;
    // Grid view data
    this.sectionId = section.dataset.sectionId;
    this.paginationType = section.dataset.paginationType;
    this.totalPages = productContainer && Number(productContainer.dataset.totalPages);
    this.view = section.dataset.view;
    // TODO: get from URL Search Params
    this.activeCol = null;
    this.currentPage = 1;
    this.initialCol = Number(section.dataset.initialColumn);
    this.cachedCol = Number(window.localStorage.getItem(this.STORAGE_KEY)) || this.initialCol;
    this.showColSwitchers = section.dataset.showColSwitchers === "true";
    // Filters data
    this.enableFilters = section.dataset.enableFilters === "true";
    this.enableSorting = section.dataset.enableSorting === "true";
    this.filtersType = section.dataset.filtersType;
    this.filtersPosition = section.dataset.filtersPosition;
  };

  initGridView = () => {
    this.toggleView(this.isDesignMode ? this.initialCol : this.cachedCol);
    this.updateViewByScreen();
    addEventDelegate({
      selector: this.selectors.cols[0],
      context: this.domNodes.toolbar,
      handler: (e, colNode) => this.toggleView(Number(colNode.dataset.column)),
    });
    this.initLoadMore();
    if (!this.sideEffectEventsAdded) {
      window.addEventListener("resize", debounce(this.updateViewByScreen, 500));
    }
  };

  initFilters = () => {
    if (!this.enableFilters) return;
    const { sidebar, openSidebar, closeSidebar } = this.domNodes;
    window.requestAnimationFrame(this.initAccordions);
    openSidebar.addEventListener("click", this.openSidebarFilter);
    closeSidebar.addEventListener("click", this.closeSidebarFilter);
    sidebar.addEventListener("click", (e) => e.target === sidebar && this.closeSidebarFilter());
  };

  initMobileSorting = () => {
    if (!this.enableSorting) return;
    const { openSorting, closeSorting, sortingWrapper } = this.mbSortingNodes;
    openSorting.addEventListener("click", this.openMobileSorting);
    closeSorting.addEventListener("click", this.closeMobileSorting);
    sortingWrapper.addEventListener("click", (e) => e.target === sortingWrapper && this.closeMobileSorting());
  };

  openMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.display = "block";
    window.requestAnimationFrame(() => {
      sortingWrapper.style.setProperty("--m-bg-opacity", "0.5");
      sortingList.style.setProperty("--m-translate-y", "0");
    });
  };

  closeMobileSorting = (e) => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.setProperty("--m-bg-opacity", "0");
    sortingList.style.setProperty("--m-translate-y", "100%");
    setTimeout(() => sortingWrapper.style.setProperty("display", "none"), 300);
  };

  initAccordions = () => {
    this.accordions && this.accordions.destroy();
    const { filtersWrapper } = this.domNodes;
    filtersWrapper.classList.remove("acc-initialized");
    this.accordions = new MinimogLibs.Accordion(filtersWrapper, {
      presetContentHeight: window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => filtersWrapper.style.setProperty('opacity', '1')
    });
  };

  openSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    sidebar.style.display = "block";
    window.requestAnimationFrame(() => {
      sidebar.style.setProperty("--m-bg-opacity", "0.5");
      sidebarContent.style.setProperty("--m-translate-x", "0");
      this.accordions && this.accordions.setContentHeight();
    });
    section.classList.add("sidebar-open");
  };

  closeSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    section.classList.remove("sidebar-open");
    if (window.innerWidth < 1280 || sidebar.dataset.type === "fixed") {
      sidebarContent.style.setProperty("--m-translate-x", "-100%");
      sidebar.style.removeProperty("--m-bg-opacity");
      setTimeout(() => sidebar.style.removeProperty("display"), 300);
    }
  };

  toggleView = (col) => {
    if (!col || this.view === "search") return;
    const { cols, productContainer } = this.domNodes;
    if (!productContainer) return;
    // Resize image product card
    const allImages = productContainer.querySelectorAll("responsive-image");
    if (allImages && allImages.length > 0) {
      allImages.forEach((img) => {
        img.disconnectedCallback();
        img.connectedCallback();
      });
    }

    const activeCol = this.activeCol || this.initialCol;
    if (this.showColSwitchers) {
      cols[activeCol - 1].classList.remove("active");
      cols[col - 1].classList.add("active");
    }

    productContainer.classList.remove(`m-cols-${activeCol}`);
    productContainer.classList.add(`m-cols-${col}`);

    window.localStorage.setItem(this.STORAGE_KEY, col);
    this.activeCol = col;
  };

  updateViewByScreen = () => {
    if (this.activeCol === 1) return; // List layout
    const device = this.getDeviceByScreenSize();
    const maxCol = this.MAX_COL_BY_SCREEN_SIZE[device];
    if (maxCol < this.activeCol) this.toggleView(maxCol);
  };

  getDeviceByScreenSize = () => {
    if (window.innerWidth < 768) return "mobile";
    if (window.innerWidth < 1024) return "tablet";
    return "desktop";
  };

  initLoadMore = () => {
    if (this.paginationType === "paginate" || this.totalPages <= 1 || this.view === "search") return;

    const { loadMoreBtn } = this.domNodes;
    loadMoreBtn.addEventListener("click", this.loadMoreProducts);

    if (this.paginationType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio === 1) this.loadMoreProducts();
          });
        },
        { threshold: 1 }
      );

      this.infiniteLoadingObserver.observe(loadMoreBtn);
    }
  };

  loadMoreProducts = () => {
    if (this.isLoadingMore) return; // NUEVO: evita peticiones simultáneas
    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) return;

    const { productContainer, loadMoreBtn } = this.domNodes;
    this.isLoadingMore = true; // NUEVO
    this.toggleLoading(true);
    fetchSection(this.sectionId, { fromCache: true, params: { page: nextPage } })
      .then((productGridHTML) => {
        const productNodes = productGridHTML.querySelectorAll("[data-product-container] .m-product-item");
        productNodes.forEach((prodNode) => {
          // NUEVO: red de seguridad contra duplicados, igual que en la restauración
          const idNode = prodNode.querySelector(".m-product-card[data-product-id]");
          const productId = idNode && idNode.getAttribute("data-product-id");
          if (productId && productContainer.querySelector(`.m-product-card[data-product-id="${productId}"]`)) {
            return;
          }
          productContainer.appendChild(prodNode);
        });
        this.initProducts();
      })
      .catch((err) => console.error(`Failed to load more products.`, err))
      .finally(() => {
        MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
        this.toggleLoading(false);
        this.currentPage = nextPage;
        this.isLoadingMore = false; // NUEVO
        if (nextPage >= this.totalPages) {
          loadMoreBtn.parentNode.remove();
          this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(loadMoreBtn);
        }
      });
  };

  // NUEVO: guarda cuántas páginas había cargadas, el scroll y la tarjeta exacta en la que se hizo clic
  saveScrollState = (productId, productHref) => {
    if (this.paginationType !== "infinite" || this.view === "search") return;
    try {
      sessionStorage.setItem(
        this.STORAGE_KEY_SCROLL,
        JSON.stringify({
          url: window.location.href,
          page: this.currentPage,
          scrollY: window.scrollY,
          productId: productId || null,
          productHref: productHref || null,
        })
      );
    } catch (e) {
      /* sessionStorage no disponible, ignorar */
    }
  };

  // NUEVO: al volver a la colección, vuelve a cargar las páginas que ya se habían visto y restaura el scroll
  restoreScrollState = () => {
    if (this.paginationType !== "infinite" || this.view === "search") return;

    let saved;
    try {
      saved = JSON.parse(sessionStorage.getItem(this.STORAGE_KEY_SCROLL));
    } catch (e) {
      saved = null;
    }

    if (!saved || saved.url !== window.location.href || saved.page <= 1) return;

    // NUEVO: se marca como "consumido" YA, antes de pedir nada — así, si esta función
    // se llegara a ejecutar dos veces por lo que sea, la segunda vez no encuentra nada
    // que restaurar y no duplica productos
    sessionStorage.removeItem(this.STORAGE_KEY_SCROLL);

    const { productContainer, loadMoreBtn } = this.domNodes;
    const targetPage = Math.min(saved.page, this.totalPages);

    // NUEVO: pausa el observador de "cargar más" mientras restauramos, para que no dispare
    // una carga en paralelo si el botón resulta visible antes de terminar (causaría duplicados)
    this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(loadMoreBtn);

    // Oculta el contenido mientras se restauran las páginas, para evitar el salto visual
    document.documentElement.classList.add("m-restoring-scroll");
    if (!document.getElementById("m-scroll-restore-style")) {
      const style = document.createElement("style");
      style.id = "m-scroll-restore-style";
      style.textContent = `
        html.m-restoring-scroll body { visibility: hidden; }
      `;
      document.head.appendChild(style);
    }

    let restoreFinished = false;

    // NUEVO: calcula y aplica la posición del producto (reutilizable, para corregir dos veces)
    const scrollToTargetProduct = () => {
      let targetY = saved.scrollY;
      let targetCard = null;

      // Prioridad 1: buscar por data-product-id (único por tarjeta, no se confunde con swatches)
      if (saved.productId) {
        targetCard = productContainer.querySelector(`.m-product-card[data-product-id="${saved.productId}"]`);
      }
      // Respaldo: buscar por href si no hay productId o no se encontró
      if (!targetCard && saved.productHref) {
        const escapedHref =
          window.CSS && CSS.escape ? CSS.escape(saved.productHref) : saved.productHref;
        const targetLink = productContainer.querySelector(`a[href="${escapedHref}"]`);
        targetCard = targetLink && targetLink.closest(".m-product-card");
      }

      const targetItem = targetCard && targetCard.closest(".m-product-item");
      if (targetItem) {
        const rect = targetItem.getBoundingClientRect();
        targetY = window.scrollY + rect.top - 120; // pequeño margen superior
      }
      window.scrollTo(0, Math.max(targetY, 0));
    };

    const finishRestore = () => {
      if (restoreFinished) return;
      restoreFinished = true;

      // Primera colocación
      scrollToTargetProduct();

      // NUEVO: corrige una segunda vez tras un breve instante, por si alguna imagen
      // que aún cargaba ha modificado la altura del bloque y ha desplazado el producto
      setTimeout(() => {
        scrollToTargetProduct();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            document.documentElement.classList.remove("m-restoring-scroll");
          });
        });
      }, 350);
    };
    // Salvaguarda: nunca deja la página oculta más de 6 segundos
    setTimeout(finishRestore, 6000);

    // Pide todas las páginas que faltan A LA VEZ (en paralelo), en vez de una por una
    const pagesToLoad = [];
    for (let p = this.currentPage + 1; p <= targetPage; p++) pagesToLoad.push(p);

    Promise.all(
      pagesToLoad.map((page) =>
        fetchSection(this.sectionId, { fromCache: true, params: { page } }).then((html) => ({ page, html }))
      )
    )
      .then((results) => {
        // Las añade en orden correcto (1, 2, 3...) aunque hayan llegado desordenadas
        results
          .sort((a, b) => a.page - b.page)
          .forEach(({ html }) => {
            const productNodes = html.querySelectorAll("[data-product-container] .m-product-item");
            productNodes.forEach((prodNode) => {
              // NUEVO: red de seguridad — si ese producto ya está en la página (por lo que sea),
              // no lo añade otra vez. Así nunca puede haber duplicados, pase lo que pase.
              const idNode = prodNode.querySelector(".m-product-card[data-product-id]");
              const productId = idNode && idNode.getAttribute("data-product-id");
              if (productId && productContainer.querySelector(`.m-product-card[data-product-id="${productId}"]`)) {
                return;
              }
              productContainer.appendChild(prodNode);
            });
          });

        this.initProducts();
        this.currentPage = targetPage;

        if (targetPage >= this.totalPages) {
          loadMoreBtn && loadMoreBtn.parentNode && loadMoreBtn.parentNode.remove();
          this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(loadMoreBtn);
        }

        MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");

        // NUEVO: si aún quedan páginas por cargar más adelante, reactiva el observador
        if (targetPage < this.totalPages && this.infiniteLoadingObserver && loadMoreBtn) {
          this.infiniteLoadingObserver.observe(loadMoreBtn);
        }

        // Da un par de frames para que el navegador termine de maquetar antes de medir la posición
        requestAnimationFrame(() => requestAnimationFrame(finishRestore));
      })
      .catch((err) => {
        console.error("Failed to restore scroll state.", err);
        // NUEVO: si algo falla, reactiva igualmente el observador para no dejar el scroll infinito roto
        if (targetPage < this.totalPages && this.infiniteLoadingObserver && loadMoreBtn) {
          this.infiniteLoadingObserver.observe(loadMoreBtn);
        }
        finishRestore();
      });
  };

  // NUEVO: conecta el guardado (clic en producto) y la restauración (vuelta atrás)
  initScrollRestoration = () => {
    if (this.paginationType !== "infinite" || this.view === "search") return;

    const { productContainer } = this.domNodes;
    if (productContainer) {
      productContainer.addEventListener(
        "click",
        (e) => {
          const link = e.target.closest('a[href*="/products/"]');
          if (!link) return;
          // Usa el data-product-id de la tarjeta completa (.m-product-card), no el href del enlace,
          // porque el href puede coincidir con una miniatura de color de OTRA tarjeta
          const card = link.closest(".m-product-card[data-product-id]");
          this.saveScrollState(card ? card.getAttribute("data-product-id") : null, link.getAttribute("href"));
        },
        true
      );
    }

    // Salvaguarda extra para navegación vía bfcache
    window.addEventListener("pagehide", () => this.saveScrollState());

    this.restoreScrollState();
  };

  initProducts = () => {
    MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
    MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
  };

  toggleLoading = (loading) => {
    const { loadMoreBtn } = this.domNodes;
    if (loadMoreBtn) {
      const method = loading ? "add" : "remove";
      loadMoreBtn.classList[method]("m-spinner-loading");
    }
  };

  handleStickyToolbar() {
    const toolbar = this.domNodes.toolbar;
    const extraSpace = toolbar && toolbar.offsetTop;
    let lastScroll = 0;
    window.addEventListener("scroll", () => {
      if (MinimogTheme.config.mqlMobile) {
        const currentScroll = window.scrollY;
        if (currentScroll <= extraSpace) {
          toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky");
          return;
        }
        toolbar.classList.add("m-collection-toolbar--sticky");
        if (
          currentScroll > toolbar.offsetHeight + extraSpace &&
          currentScroll > lastScroll &&
          !toolbar.classList.contains("scroll-down")
        ) {
          toolbar.classList.remove("scroll-up");
          toolbar.classList.add("scroll-down");
        } else if (currentScroll < lastScroll && toolbar.classList.contains("scroll-down")) {
          toolbar.classList.remove("scroll-down");
          toolbar.classList.add("scroll-up");
        }
        lastScroll = currentScroll;
      } else {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky", "scroll-down");
      }
    });
  }
}